"use client";

import { useQuery } from "@apollo/client";
import { MiniSearchDocument, type MiniSearchQuery, type MiniSearchQueryVariables } from "@pimcore";
import { formatLocale } from "@tosize/ui";
import { usePathname } from "next/navigation";
import { useCallback, useState } from "react";
import TagManager from "react-gtm-module";
import { useDebounceCallback } from "usehooks-ts";
import { track } from "~/app/actions/tracking";
import { useQuery as useRouteQuery } from "~/hooks/useQuery";

export default function useSearch(locale: string) {
	const pathname = usePathname();
	const { searchParams } = useRouteQuery();
	const q = searchParams?.get("q");
	const [searchText, setSearchText] = useState(q ?? "");
	const [query, setQuery] = useState(searchText);
	const [localData, setLocalData] = useState<MiniSearchQuery>();

	const { loading } = useQuery<MiniSearchQuery, MiniSearchQueryVariables>(MiniSearchDocument, {
		variables: {
			query: query,
			defaultLanguage: formatLocale(locale, "language_COUNTRY"),
		},
		context: { clientName: "pim" },
		skip: !query,
		onCompleted: (result) => {
			if (result.miniSearch) {
				TagManager.dataLayer({ dataLayer: { event: "miniSearch" } });

				track({
					lang: locale,
					events: [
						{
							name: "server_mini_search",
							params: { search_term: query, page_location: pathname },
						},
					],
				});
			}

			setLocalData(result);
		},
		onError: () => setLocalData(undefined),
	});

	const setDebouncedQuery = useDebounceCallback(setQuery, 1000);

	const searchTextHandler = useCallback(
		(text: string) => {
			setSearchText(text);
			if (!text) setLocalData(undefined);

			return localData ? setDebouncedQuery(text) : setQuery(text);
		},
		[localData, setDebouncedQuery],
	);

	const resetState = useCallback(() => {
		setSearchText("");
		setLocalData(undefined);
	}, []);

	return { resetState, searchTextHandler, localData, searchText, loading };
}
